/**
 * The crops inside the feature cells: pieces of the real interface, built
 * from the same parts the product is built from.
 *
 * The four they replace were 755 lines of hand-drawn mockup with animated
 * counters, a dot heatmap and a bar chart drawn for the occasion. None of it
 * was the product, all of it went stale the week the interface changed, and
 * the counters broke the one motion rule this system has.
 *
 * The grid they sit in is no longer four equal cells. One claim leads at the
 * width of the row and three support it underneath, so the section has a way
 * in. The lead is the only one whose picture is whole rather than cropped.
 *
 * The figures are stand-ins for a fictional site. They used to carry a label
 * saying so under every cell; four of them in one grid read as hedging rather
 * than as honesty, and the card that leads the section says "sample page" on
 * its own address line.
 */
import { BotMark, Ranked } from '@/design-system/bots'
import { Bars } from '@/design-system/appcards'
import { Dial } from '@/design-system/domain'
import { PutBack } from '@/design-system/market'
import { Nights } from '@/design-system/record'

/*
  The lead. One page, one fact that stopped being true, and the record of
  every night we looked.

  The correction and the record belong together: the strip underneath is not
  a second claim, it is the evidence for the one above it. Three eventful
  nights in sixty, each of them a card like the one above.

  Sixty nights, not the full six months. The specimen learned this the hard
  way at full container width: 182 ticks in a column about this wide put each
  tick under a pixel and the quiet nights vanished into a smear. A crop is a
  crop, so it shows fewer nights at a size where a night still reads as a
  night.
*/
const EVENTFUL = new Set([11, 38, 52])
const TWO_MONTHS = Array.from({ length: 60 }, (_, i) => EVENTFUL.has(i))

export function BreakCrop() {
  return (
    <PutBack
      page="crewline.io/pricing"
      says="From $49 per seat, per month"
      told="From $39 per seat, per month"
      when="Put back last night"
      next="We check again tonight, and you only hear from us if something changes."
    />
  )
}

/* The record goes under the words rather than under the card: stacked, the
   two of them left the lead's other column half empty. */
export function BreakRecord() {
  return <Nights nights={TWO_MONTHS} from="1 July" to="Last night" />
}

/*
  Where the words come from. The page's own sentence, lifted and quoted, then
  the two details taken out of it, matching. No other crop in this grid is
  shaped like a quotation, which is the point: four crops that were all
  horizontal bars read as one picture repeated.
*/
export function WrittenCrop() {
  return (
    <div style={{ padding: '16px 4px', display: 'grid', gap: 16 }}>
      <div>
        <p className="ds-small ds-faint">Your page says</p>
        <p
          className="ds-body"
          style={{
            marginTop: 6,
            paddingLeft: 14,
            borderLeft: '2px solid var(--ds-rule-2)',
            fontSize: 16,
            lineHeight: '25px',
          }}
        >
          Plans start at $49 per seat, per month, billed annually.
        </p>
      </div>
      <div>
        <p className="ds-small ds-faint" style={{ marginBottom: 2 }}>
          AI agents are told
        </p>
        {[
          ['Price', '$49 per seat, per month'],
          ['Billing', 'Annual'],
        ].map(([label, value]) => (
          <div
            key={label}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 14,
              padding: '9px 0',
              borderTop: '1px solid var(--ds-rule)',
              fontSize: 15,
              lineHeight: '22px',
            }}
          >
            <span style={{ color: 'var(--ds-ink-2)', flex: 'none' }}>{label}</span>
            <span style={{ fontWeight: 500, textAlign: 'right', minWidth: 0 }}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export function ReadersCrop() {
  return (
    <div style={{ padding: '16px 4px' }}>
      <Ranked
        rows={[
          { what: 'ChatGPT', n: 412, mark: <BotMark name="ChatGPT" /> },
          { what: 'Perplexity', n: 188, mark: <BotMark name="Perplexity" /> },
          { what: 'Claude', n: 96, mark: <BotMark name="Claude" /> },
          { what: 'Gemini', n: 74, mark: <BotMark name="Gemini" /> },
        ]}
      />
    </div>
  )
}

/*
  The score, and what it is a score OF.

  It used to be a dial reading 73 beside the words "out of 100", which told a
  reader the range and nothing else: 73 out of 100 of what, and what would
  make it 90? The five signals underneath are the answer to both. They are the
  same five the audit scores and the methodology page names, so the number now
  has a referent and the weakest bar is the "clearest next thing to do about
  it" that the cell above it promises, without us having to write a claim.
*/
export function ScoreCrop() {
  return (
    <div style={{ padding: '16px 4px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <Dial value={73} size={64} />
        <div style={{ minWidth: 0 }}>
          <p className="ds-body" style={{ margin: 0, fontWeight: 500 }}>
            SuperSchema Score
          </p>
          <p className="ds-small ds-faint" style={{ marginTop: 2 }}>
            out of 100, across five signals
          </p>
        </div>
      </div>
      <div style={{ marginTop: 16 }}>
        <Bars
          rows={[
            { label: 'Identity', value: 88 },
            { label: 'Structure', value: 72 },
            { label: 'Access', value: 91 },
            { label: 'Consistency', value: 74 },
            { label: 'Revenue Clarity', value: 68 },
          ]}
        />
      </div>
    </div>
  )
}
